import React, { useEffect, useState } from "react";
import { Input, Button } from "@material-tailwind/react";
import { MdClose, MdEdit } from "react-icons/md";
import axios from "axios";
import { EDIT_PRODUCT_API } from "../../Api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EditProduct = ({ prodData, categoryId, subCategoryId, onClose }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // features
  const [featureInput, setFeatureInput] = useState(""); // input field
  const [features, setFeatures] = useState([]); // list of features

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    setName(prodData.name || "");
    setDescription(prodData.desc || "");
    setFeatures(Array.isArray(prodData.feat) ? prodData.feat : []); // ✅ features array
    setExistingImages(prodData.img || []);
    setPreviewImages(prodData.img || []);
    setMainImage(prodData.img?.[0] || "");
  }, [prodData]);

  // Handle selecting multiple new images
const handleImageChange = (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;

  // ✅ filter out oversized images
  const oversized = files.filter((file) => file.size > 1 * 1024 * 1024);
  if (oversized.length > 0) {
    toast.warning("Some files are larger than 1MB. Please upload smaller images.");
    return;
  }

  setNewImages((prev) => [...prev, ...files]);

  const newPreviews = files.map((file) => URL.createObjectURL(file));
  setPreviewImages((prev) => [...prev, ...newPreviews]);

  if (!mainImage) setMainImage(newPreviews[0]);
};


  // Remove an image (either existing or new)
  const handleRemoveImage = (img) => {
    if (existingImages.includes(img)) {
      setExistingImages(existingImages.filter((i) => i !== img));
      setPreviewImages(previewImages.filter((i) => i !== img));
    } else {
      const index = previewImages.indexOf(img);
      if (index > -1) {
        const newPreview = [...previewImages];
        newPreview.splice(index, 1);
        setPreviewImages(newPreview);

        const newFiles = [...newImages];
        newFiles.splice(index - existingImages.length, 1);
        setNewImages(newFiles);
      }
    }

    if (mainImage === img) {
      setMainImage(previewImages[0] || "");
    }
  };

  const handleUpdate = async () => {
    const formData = new FormData();
    formData.append("productName", name);
    formData.append("productDescription", description);

    // ✅ send features as JSON string
    features.forEach((f) => {
      formData.append("productFeatures[]", f);
    });

    formData.append("categoryId", categoryId);
    formData.append("subCategoryId", subCategoryId);
    formData.append("productId", prodData.id);

    existingImages.forEach((img) =>
      formData.append("productExistingImgUrl", img)
    );
    newImages.forEach((file) => {
      formData.append("productNewImages", file);
    });

    try {
      const response = await axios.post(EDIT_PRODUCT_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Product updated successfully:", response.data);
      toast.success("Product updated successfully");
      onClose();
    } catch (error) {
      toast.error("Unable to update product");
      console.error("Error updating product:", error);
    }
  };

  // ✅ Add feature
  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFeatures((prev) => [...prev, featureInput.trim()]);
      setFeatureInput("");
    }
  };

  // ✅ Remove feature
  const handleRemoveFeature = (index, e) => {
    e.preventDefault();
    setFeatures((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-[1000] flex">
      <div
        className="hidden sm:block flex-grow bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div className="relative h-full w-full sm:w-[650px] bg-white shadow-lg flex flex-col">
        {/* Close Btn */}
        <button
          className="absolute top-4 right-4 text-gray-600 hover:text-red-500"
          onClick={onClose}
        >
          <MdClose className="w-6 h-6" />
        </button>

        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ maxHeight: "calc(100vh - 60px)" }}
        >
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Edit Product
          </h2>
          <hr className="mb-4 border-gray-300" />

          {/* Main Image */}
          <div className="relative w-full h-56 rounded-lg overflow-hidden shadow-md mb-4">
            {mainImage && (
              <img
                src={mainImage}
                alt="Main Product"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Thumbnails */}
          <div className="flex gap-3 flex-wrap mb-4">
            {previewImages.map((img, idx) => (
              <div
                key={idx}
                className={`relative w-20 h-20 rounded-md overflow-hidden border cursor-pointer ${mainImage === img ? "ring-2 ring-green-500" : ""
                  }`}
                onClick={() => setMainImage(img)}
              >
                <img
                  src={img}
                  alt={`thumb-${idx}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(img);
                  }}
                  className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <MdClose size={14} />
                </button>
              </div>
            ))}

            <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer">
              <MdEdit className="w-6 h-6 text-gray-500" />
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          {/* Name (Read-Only) */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-1">
              Product Name
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-blue-50 text-gray-700 font-semibold border border-blue-300"
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-1">
              Description
            </label>
            <textarea
              className="w-full border p-2 rounded-lg"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></textarea>
          </div>

          {/* Features */}
          <div className="flex-1">
            <div className="relative flex flex-col w-full">
              <div className="flex items-center">
                <Input
                  type="text"
                  label="Features"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  className="border p-3 flex-grow rounded-md w-full"
                />
                <Button
                  type="button"
                  onClick={handleAddFeature}
                  className="absolute right-4 bg-green-500 text-white px-4 py-1 rounded-md"
                >
                  Add
                </Button>
              </div>

              {/* ✅ Tip below field */}
              <p className="text-xs text-gray-500 mt-1">
                Tip: Use <span className="font-semibold">":"</span> to separate a heading and its detail.
                Example → <code className="bg-gray-100 px-1 py-0.5 rounded">Heading: Feature1</code>
              </p>
            </div>


            <ul className="list-disc pl-5 mt-3 text-gray-600">
              {features.map((item, index) => (
                <li key={index} className="flex items-center">
                  <span>{item}</span>
                  <button
                    onClick={(e) => handleRemoveFeature(index, e)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    ✖
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="absolute bottom-0 left-0 w-full bg-white shadow-md flex px-4 py-4 gap-3 border-t border-gray-200">
          <button
            className="flex-1 bg-gray-400 text-white py-3 rounded-lg hover:bg-gray-500"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600"
            onClick={handleUpdate}
          >
            Update
          </button>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EditProduct;
